import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { ParallaxScoped } from '@r1-frontend/ui-react/components/animation/Parallax';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { H4 } from '@r1-frontend/ui-react/components/typography/heading';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { TState } from '~/src/store';
import { getCurrentCity } from '~/src/store/selectors/city';
import Management from '~/static/images/internet/v2/banners/app/management.png';
import ManagementWebp from '~/static/images/internet/v2/banners/app/management.webp';
import Favorites from '~/static/images/internet/v2/banners/entertainment/favorites.png';
import FavoritesWebp from '~/static/images/internet/v2/banners/entertainment/favorites.webp';

import Section from './components/Section';
import { DoubleColumnBannersContainer } from './containers/DoubleColumnBannersContainer';
import { FAQContainer } from './containers/FAQContainer';
import { InternetBenefitsContainer } from './containers/InternetBenefitsContainer';
import { RoutersContainer } from './containers/RoutersContainer';
import {
    DesktopSpeedLineContainer,
    MobileSpeedLineContainer,
} from './containers/SpeedLineContainer';
import { TariffsContainer } from './containers/TariffsContainer';
import { getRouters } from './store/actions/thunk';
import { features, subscriptions } from './data';

import * as ST from './styled';

export const Internet = (): JSX.Element => {
    const dispatch = useDispatch();
    const ref = useRef<HTMLDivElement | null>(null);

    const { sections, cityIn } = useSelector((state: TState) => ({
        sections: Object.keys(state.internetReducer.sections),
        cityIn: getCurrentCity(state)?.cityIn || 'городе',
    }));

    const isMobile = useMatchMediaByWidth(wideBreakpoints.mobile);

    useEffect(() => {
        dispatch(getRouters());
    }, []);

    const handleOnBannerClick = (label: string) => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: 'tariff_banner',
            action: 'click_choose_tariff',
            label,
        });
    };

    const handleOnAppBannerClick = () => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: 'internet',
            action: 'click_link_install_app',
        });
    };

    const childSeoHeader = <H4 $smaller as={'h1'} $color={'TextSecondary'}>Домашний интернет в {cityIn}</H4>;

    return (
        <ParallaxScoped root={ref}>
            <ST.Internet ref={ref}>
                {
                    isMobile
                        ? <MobileSpeedLineContainer>{childSeoHeader}</MobileSpeedLineContainer>
                        : <DesktopSpeedLineContainer>{childSeoHeader}</DesktopSpeedLineContainer>
                }

                <Section
                    chat={{
                        out: <>&laquo;Интернет, каким он&nbsp;должен быть&raquo;&nbsp;&mdash; это какой?</>,
                        in: <>Стабильный, скоростной и&nbsp;управляемый. И&nbsp;это не&nbsp;просто слова:</>,
                    }}
                    $key={sections[0]}
                >
                    <InternetBenefitsContainer onBannerClick={handleOnBannerClick} />
                </Section>

                <Section
                    wide
                    chat={{
                        out: <>А&nbsp;что, можно подключиться онлайн?</>,
                        in: <>Конечно! Но&nbsp;для начала нужно выбрать тариф:</>,
                    }}
                    root={ref}
                    $key={sections[1]}
                >
                    <TariffsContainer />
                </Section>

                <Section
                    root={ref}
                    chat={{
                        out: <>А&nbsp;вот вы&nbsp;ещё говорили, что интернет управляемый. Это как?</>,
                        in: <>Конечно, одно приложение для всего, что связано с&nbsp;интернетом.</>,
                    }}
                    $key={sections[2]}
                >
                    <DoubleColumnBannersContainer
                        staticBanner={{
                            backgroundImage: Management,
                            backgroundImageWebp: ManagementWebp,
                            title: 'Ваш центр управления интернетом',
                            description:
                                <>
                                    Домашний интернет и&nbsp;другие услуги в&nbsp;одном удобном приложении
                                </>,
                            buttonText: 'Установить',
                            buttonHref: '/service/agent',
                            onButtonClick: handleOnAppBannerClick,
                        }}
                        carouselBanners={features}
                    />
                </Section>

                <Section
                    wide
                    chat={{
                        out: <>Что если у&nbsp;меня нет роутера, но&nbsp;нужен Wi-Fi?</>,
                        in:
                            <>
                                У&nbsp;нас есть роутеры&nbsp;&mdash; ловят
                                везде, можно подключить
                                до&nbsp;200&nbsp;устройств, управляются
                                в&nbsp;приложении
                            </>,
                    }}
                    $key={sections[3]}
                    root={ref}
                >
                    <RoutersContainer />
                </Section>

                <Section
                    chat={{
                        out: <>А&nbsp;что ещё у&nbsp;вас есть?</>,
                        in: <>Всё, чтобы развлекаться и&nbsp;отдыхать, не&nbsp;выходя из&nbsp;дома</>,
                    }}
                    $key={sections[4]}
                    root={ref}
                >
                    <DoubleColumnBannersContainer
                        staticBanner={{
                            backgroundImage: Favorites,
                            backgroundImageWebp: FavoritesWebp,
                            title: 'Ваши любимые подписки',
                            description:
                                <>
                                    Онлайн-кинотеатры, пакеты каналов, книги, игры и&nbsp;полезные сервисы в&nbsp;вашем тарифе
                                </>,
                            buttonText: 'Выбрать тариф',
                            buttonHref: '/bundles',
                            onButtonClick: () => handleOnBannerClick('Ваши любимые подписки'),
                        }}
                        carouselBanners={subscriptions}
                    />
                </Section>

                <Section
                    wide
                    background={COLORS.BgSurface}
                    $key={sections[5]}
                    root={ref}
                >
                    <FAQContainer />
                </Section>
            </ST.Internet>
        </ParallaxScoped>
    );
};
